import React, { Fragment } from 'react';
import { useParams } from 'react-router-dom';

import axios from 'axios';
import { useQuery } from '@tanstack/react-query';

import { Popover, Transition } from '@headlessui/react';
import { Info } from 'lucide-react';

import Spinner from '../../../components/Spinner';
import ErrorBox from '../../../components/ErrorBox';
import RadialChart from './RadialChart';
import LinearChart from './LinearChart';

import { lineChartTypes, radialTypes } from '../../../constants/Dashboard';

const tooltip = (
  <ul>
    <li>0% to 49%: Poor</li>
    <li>50% to 69%: OK</li>
    <li>70% to 89%: Good</li>
    <li>90% to 100%: Excellent</li>
  </ul>
);

const reputationScore = {
  poor: {
    value: 'Poor',
    description: 'Your online reputation is falling behind and needs improvement. Check out the Recommendation section below.',
    textColor: 'rgba(255, 0, 0)',
    bgColor: 'rgba(255, 0, 0, 0.2)'
  },
  ok: {
    value: 'OK',
    description: 'Your online reputation meets the basic standards but there is room for improvement. Check out the Recommendations section below.',
    textColor: 'rgba(250, 140, 21)',
    bgColor: 'rgba(250, 140, 21, 0.2)'
  },
  good: {
    value: 'Good',
    description: 'Your online reputation is solid, exceeding basic standards.',
    textColor: 'rgba(32, 201, 151)',
    bgColor: 'rgba(32, 201, 151, 0.2)'
  },
  excellent: {
    value: 'Excellent',
    description: 'Congratulations! Your online reputation is exceptional!',
    textColor: 'rgba(0, 102, 0)',
    bgColor: 'rgba(0, 102, 0, 0.2)'
  }
};

const getGradient = color => `linear-gradient(0deg, ${color} 0%, rgba(255, 255, 255, 0) 100%), linear-gradient(0deg, #fffcfc, #fffcfc)`;

const evalReputation = percent => {
  switch (true) {
    case percent >= 0 && percent <= 49:
      return reputationScore.poor;
    case percent >= 50 && percent <= 69:
      return reputationScore.ok;
    case percent >= 70 && percent <= 89:
      return reputationScore.good;
    case percent >= 90 && percent <= 100:
      return reputationScore.excellent;
    default:
      return;
  }
};

const renderRadialLegend = (industryAverage, youAverage) => {
  return (
    <div className="flex flex-col justify-center gap-y-4 max-[1650px]:flex-row max-[1650px]:gap-x-2.5 max-[1250px]:flex-col">
      <div className="flex w-max flex-col gap-y-2">
        <span className="flex items-center text-left text-sm font-normal leading-[18px]">
          <div className="mr-[5px] h-2 w-2 rounded-full bg-[#20c997]"></div>Your average:</span>
        <span className="pl-4 text-left text-base font-semibold leading-5 tracking-[0.2px]">{youAverage}%</span>
      </div>
      <div className="flex w-max flex-col gap-y-2">
        <span className="flex items-center text-left text-sm font-normal leading-[18px]">
          <div className="mr-[5px] h-2 w-2 rounded-full bg-[#fa8c15]"></div>Industry average:</span>
        <span className="pl-4 text-left text-base font-semibold leading-5 tracking-[0.2px]">{industryAverage}%</span>
      </div>
    </div>
  );
};

const fetchReputationScore = async ({ queryKey }) => {
  const [, locationId] = queryKey;
  
  const { data } = await axios.get(`${import.meta.env.VITE_API_URL}api/v4/locachecker/reputationScore`, {
    params: { locationId },
  });
  
  return data.data;
};

const ReputationScore = () => {
  const { locationId } = useParams();
  
  const {
    data: reputationScore,
    isLoading,
    isError
  } = useQuery({
    queryKey: ['reputationScore', locationId],
    queryFn: fetchReputationScore,
    enabled: !!locationId,
  });
  
  if (isLoading) {
    return <Spinner height={635}/>;
  }
  
  if (isError) {
    return <ErrorBox description="We encountered an issue loading your Reputation Score. Please try again."/>;
  }
  
  const reputation = evalReputation(reputationScore?.total[1].value);
  
  return (
    <div className="flex flex-col gap-y-10 rounded-md bg-white p-8 px-10 shadow-[0px_4px_8px_0px_#0000000D]">
      <div className="flex items-center gap-x-5 max-[1500px]:flex-col max-[1500px]:gap-y-5 min-[2200px]:gap-x-[60px]">
        
        <div
          className="flex w-full max-w-[250px] flex-col max-[1500px]:max-w-none max-[768px]:text-center min-[1800px]:max-w-[350px] min-[2200px]:w-auto min-[2200px]:max-w-none">
          <h2 className="mb-3 text-2xl font-medium leading-[31px]">Welcome!</h2>
          <span className=" text-[#545454] mb-2 text-[15px] font-normal leading-6">{reputation?.description}</span>
        </div>
        
        <div
          className="flex w-full items-center justify-between gap-x-5 max-[1250px]:flex-wrap max-[1250px]:justify-start max-[1250px]:gap-[30px] max-[768px]:justify-center min-[2200px]:gap-x-10">
          <div className="flex h-[222px] w-full max-w-[222px] flex-col items-center gap-y-6 rounded-md border border-[#e4e4e4] p-6"
               style={{ background: getGradient(reputation?.bgColor) }}>
            <span className="text-lg font-medium leading-6 text-black">
              Reputation Score
             <Popover className="relative inline-flex">
                <Popover.Button className="ml-1 inline-flex align-top">
                  <Info className="h-[14px] w-[14px] text-gray-400"/>
                </Popover.Button>
                <Transition as={Fragment} /* ... */ >
                  <Popover.Panel className="absolute bottom-full left-1/2 z-10 mb-2 w-max -translate-x-1/2 transform px-4">
                    <div className="w-max rounded-lg bg-gray-800 p-3 text-xs font-normal text-white shadow-lg">
                      {tooltip}
                    </div>
                  </Popover.Panel>
                </Transition>
              </Popover>
            </span>
            
            <span
              className="font-medium leading-[83px] tracking-[0.1166em]"
              style={{
                color: reputation?.textColor,
                fontSize: reputation?.value === 'Excellent' ? '42px' : '64px'
              }}
            >
              {reputation?.value}
            </span>
          </div>
          
          <div className="flex items-center max-[1650px]:flex-col-reverse max-[1250px]:flex-row">
            {renderRadialLegend(reputationScore?.total[0]?.value, reputationScore?.total[1]?.value)}
            
            <RadialChart type={radialTypes.img} data={reputationScore?.total}/>
          </div>
          
          <LinearChart type={lineChartTypes.double} data={reputationScore?.averagePerPeriod} className="max-w-[800px]"/>
        </div>
      </div>
      
      <div
        className="flex justify-between max-[1600px]:flex-wrap max-[1600px]:justify-center max-[1600px]:gap-x-[60px] max-[1200px]:gap-x-0 min-[2200px]:justify-evenly">
        
        {reputationScore?.metricsData.map((item, i) => (
          <RadialChart key={i} type={radialTypes.text} data={item.data} title={item.title}/>
        ))}
      </div>
    </div>
  );
};

export default ReputationScore;
